// src/lib/utils/visitorCheck.ts
//
// docs/visitor-check-choice_plan.md Phase 1, task 1.3: which visitor check a
// form actually uses. `settings.visitorCheck` is an explicit, present-tense
// choice the owner made in the new dropdown (Phase 3); a form saved before
// that dropdown existed has no such value and falls back to the old
// `turnstileEnabled` flag, so every existing form resolves to exactly the
// check it has today. "builtin" is therefore only ever reached by an
// explicit save, never implied by the old flag -- the same invariant
// `GET /api/forms/[id]/altcha` (task 1.2) already relies on by reading
// `settings.visitorCheck` directly rather than through this function.

import type { FormSettings } from "@/lib/types";

export type VisitorCheck = NonNullable<FormSettings["visitorCheck"]>;

/**
 * Resolves the visitor check a form actually uses. Accepts a structural
 * subset of `TypedForm` (like `firstPartyTurnstileSiteKey` in
 * src/lib/utils/turnstile.ts) so callers with only a settings blob, not a
 * full form row, can use it too.
 *
 * This file is bundled into the public embed (src/scripts/kiroku-embed.ts
 * imports effectiveVisitorCheck directly, so esbuild pulls in everything this
 * module imports at the top level). The plan-gate helpers for task 1.2 of
 * docs/visitor-check-defaults_plan.md live in the sibling
 * cloudflareCheckPlan.ts instead of here for exactly that reason: importing
 * `@/config/plans` from this file pulled PLAN_LIMITS -- Stripe price ids,
 * currency tables, retention constants and all -- into every visitor's
 * browser, +3.5KB on public/embed.js, caught by
 * tests/guard/embed-bundle-is-current.test.ts. Keep this file free of a
 * `@/config/plans` import.
 */
export function effectiveVisitorCheck(form: {
  settings?: Pick<FormSettings, "visitorCheck" | "turnstileEnabled"> | null;
}): VisitorCheck {
  const explicit = form.settings?.visitorCheck;
  if (explicit) return explicit;
  return form.settings?.turnstileEnabled ? "cloudflare" : "none";
}

/**
 * The built-in check is the default for a form created with no explicit
 * `visitorCheck` (docs/visitor-check-defaults_plan.md task 1.3). Callers apply
 * this only at a creation path that mints a form the owner will actually use
 * (the dashboard create route, the AI form generator) -- never for a
 * HITL-minted form, and never for an existing form's update. An explicit
 * choice already present in `settings`, including an explicit "none", always
 * wins.
 */
export function withDefaultVisitorCheck(
  settings: FormSettings | null | undefined,
): FormSettings {
  return {
    ...(settings ?? {}),
    visitorCheck: settings?.visitorCheck ?? "builtin",
  };
}
